/**
 * pdf.js
 * Generates a clean cost quote PDF using jsPDF.
 * Pro feature — caller must check isPro() before invoking.
 */

import { formatDollar } from './calculator.js'

export async function exportPDF(inputs, result, jobLabel = 'Print Job Quote') {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })

  const PAGE_W = 210
  const MARGIN = 20
  const COL_R  = PAGE_W - MARGIN
  let y = MARGIN

  const line = (text, x, yPos, opts = {}) => doc.text(text, x, yPos, opts)

  // ---------------------------------------------------------------------------
  // Brand colours
  // ---------------------------------------------------------------------------
  const GRAPHITE     = [28,  43,  43]   // #1C2B2B
  const GRAPHITE_MID = [46,  64,  64]   // #2E4040
  const TEAL         = [13,  140, 140]  // #0D8C8C
  const TEAL_LIGHT   = [18,  170, 170]  // #12AAAA
  const SLATE        = [74,  96,  96]   // #4A6060
  const OFF_WHITE    = [245, 247, 247]  // #F5F7F7
  const WHITE        = [255, 255, 255]

  // ---------------------------------------------------------------------------
  // Header bar — full width graphite
  // ---------------------------------------------------------------------------
  doc.setFillColor(...GRAPHITE)
  doc.rect(0, 0, PAGE_W, 28, 'F')

  // Mark A spool — drawn with circles and lines
  const mx = MARGIN + 7
  const my = 14
  doc.setFillColor(...TEAL)
  doc.circle(mx, my, 6.5, 'F')
  doc.setFillColor(...GRAPHITE)
  doc.circle(mx, my, 4.6, 'F')
  doc.setFillColor(...GRAPHITE_MID)
  doc.circle(mx, my, 2.9, 'F')
  doc.setDrawColor(...TEAL_LIGHT)
  doc.setLineWidth(0.5)
  ;[[-1.4], [0], [1.4]].forEach(([offset]) => {
    doc.line(mx - 2.2, my + offset, mx + 2.2, my + offset)
  })
  doc.setFillColor(...GRAPHITE)
  doc.circle(mx, my, 0.9, 'F')

  // Wordmark
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(...WHITE)
  line('PrintCalc', MARGIN + 16, my + 1.5)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(...TEAL_LIGHT)
  line('by SpoolLedger', MARGIN + 16, my + 6.5)

  // Date — right aligned
  doc.setFontSize(8)
  doc.setTextColor(...SLATE)
  line(
    new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    }),
    COL_R, my + 1.5, { align: 'right' }
  )

  y = 38

  // ---------------------------------------------------------------------------
  // Job name
  // ---------------------------------------------------------------------------
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.setTextColor(...GRAPHITE)
  line(jobLabel, MARGIN, y)
  y += 4

  doc.setDrawColor(...TEAL)
  doc.setLineWidth(0.4)
  doc.line(MARGIN, y, MARGIN + 40, y)
  y += 8

  // ---------------------------------------------------------------------------
  // Two column layout — inputs left, breakdown right
  // ---------------------------------------------------------------------------
  const COL2_X = 115
  const COL2_R = COL_R
  const sectionY = y

  // Left column — Job inputs
  doc.setFillColor(...OFF_WHITE)
  doc.roundedRect(MARGIN, y - 2, 85, 4, 0.5, 0.5, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  doc.setTextColor(...TEAL)
  line('JOB INPUTS', MARGIN + 2, y + 1.5)
  y += 8

  const inputRows = [
    ['Filament cost',    `$${inputs.filCost}/kg`],
    ['Grams used',       `${inputs.grams} g`],
    ['Print time',       `${inputs.hours} hrs`],
    ['Printer wattage',  `${inputs.watts} W`],
    ['Electricity rate', `$${inputs.elecRate}/kWh`],
    ['Profit margin',    `${inputs.marginPct}%`],
    ['Overhead rate',    `${inputs.overheadPct}%`],
  ]

  if (inputs.failRate > 0) inputRows.push(['Failure buffer', `${inputs.failRate}%`])
  if (inputs.laborHrs > 0) {
    inputRows.push(['Labor time',  `${inputs.laborHrs} hrs`])
    inputRows.push(['Labor rate',  `$${inputs.laborRate}/hr`])
  }

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)

  inputRows.forEach(([label, val], i) => {
    if (i % 2 === 0) {
      doc.setFillColor(250, 252, 252)
      doc.rect(MARGIN, y - 3, 85, 5.5, 'F')
    }
    doc.setTextColor(...SLATE)
    line(label, MARGIN + 2, y + 1)
    doc.setTextColor(...GRAPHITE)
    line(val, MARGIN + 83, y + 1, { align: 'right' })
    y += 5.5
  })

  // Right column — Cost breakdown
  let ry = sectionY

  doc.setFillColor(...OFF_WHITE)
  doc.roundedRect(COL2_X, ry - 2, COL2_R - COL2_X, 4, 0.5, 0.5, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  doc.setTextColor(...TEAL)
  line('COST BREAKDOWN', COL2_X + 2, ry + 1.5)
  ry += 8

  const breakdownRows = [
    ['Material cost',    result.matCost],
    ['Electricity cost', result.elecCost],
  ]
  if (result.laborCost > 0)  breakdownRows.push(['Labor cost',    result.laborCost])
  if (result.failBuffer > 0) breakdownRows.push(['Failure buffer', result.failBuffer])
  breakdownRows.push(['Overhead', result.overheadAmt])

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)

  breakdownRows.forEach(([label, val], i) => {
    if (i % 2 === 0) {
      doc.setFillColor(250, 252, 252)
      doc.rect(COL2_X, ry - 3, COL2_R - COL2_X, 5.5, 'F')
    }
    doc.setTextColor(...SLATE)
    line(label, COL2_X + 2, ry + 1)
    doc.setTextColor(...GRAPHITE)
    line(formatDollar(val), COL2_R, ry + 1, { align: 'right' })
    ry += 5.5
  })

  // Total cost divider
  ry += 2
  doc.setDrawColor(...TEAL)
  doc.setLineWidth(0.3)
  doc.line(COL2_X, ry, COL2_R, ry)
  ry += 4

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(...GRAPHITE)
  line('Total cost', COL2_X + 2, ry)
  line(formatDollar(result.totalCost), COL2_R, ry, { align: 'right' })
  ry += 5

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(...SLATE)
  line(`Profit at ${result.marginPct}% margin`, COL2_X + 2, ry)
  line(formatDollar(result.profitAmt), COL2_R, ry, { align: 'right' })

  // ---------------------------------------------------------------------------
  // Suggested sale price — hero card
  // ---------------------------------------------------------------------------
  const heroY = Math.max(y, ry) + 12

  doc.setFillColor(...GRAPHITE)
  doc.roundedRect(MARGIN, heroY, PAGE_W - MARGIN * 2, 22, 2, 2, 'F')

  // Teal left accent bar
  doc.setFillColor(...TEAL)
  doc.roundedRect(MARGIN, heroY, 2, 22, 1, 1, 'F')

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...SLATE)
  line('Suggested sale price', MARGIN + 6, heroY + 8)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(74, 96, 96)
  line(
    `at ${result.marginPct}% margin · ${result.overheadPct}% overhead · break-even ${formatDollar(result.totalCost)}`,
    MARGIN + 6, heroY + 14
  )

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(22)
  doc.setTextColor(...TEAL_LIGHT)
  line(formatDollar(result.salePrice), COL_R - 4, heroY + 15, { align: 'right' })

  // ---------------------------------------------------------------------------
  // Footer
  // ---------------------------------------------------------------------------
  const footerY = 282

  doc.setDrawColor(...GRAPHITE_MID)
  doc.setLineWidth(0.2)
  doc.line(MARGIN, footerY, COL_R, footerY)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.setTextColor(...SLATE)
  line('Generated by PrintCalc', MARGIN, footerY + 5)

  doc.setTextColor(...TEAL)
  line('calc.spoolledger.com', MARGIN + 35, footerY + 5)

  doc.setTextColor(...SLATE)
  line('3D print pricing for makers, Etsy sellers, and small print farms', COL_R, footerY + 5, { align: 'right' })

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const filename = `${jobLabel.replace(/\s+/g, '_')}_${Date.now()}.pdf`
  doc.save(filename)
}